import clsx from "clsx";
import { FormEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Checkbox,
  ConfirmDialog,
  Field,
  FileUpload,
  FormActions,
  FormSection,
  InlineBanner,
  Input,
  LanguageTabs,
  notify,
  PageHeader,
  Segmented,
  Select,
  Skeleton,
  SkeletonRegion,
  StateBlock,
  Textarea,
} from "../../components/ui";
import { CURRENCIES } from "../../constants/currencies";
import { PREVIEW_LANGUAGES } from "../../constants/languages";
import { useTheme } from "../../lib/theme";
import { useApiError } from "../../hooks/useApiError";
import useAuth from "../../hooks/useAuth";
import { usePageTitle } from "../../hooks/usePageTitle";
import api, { asPlanLimit } from "../../services/api";
import { PLANS, type Plan } from "../../types/Restaurant";
import type { RestaurantRecord } from "../Locations/apiTypes";
import { useRequest } from "../Locations/useRequest";

const DESCRIPTION_MAX = 300;

interface Text {
  name: string;
  description: string;
}

type Position = "before" | "after";

interface Values {
  languages: string[];
  defaultLanguage: string;
  texts: Record<string, Text>;
  currency: string;
  timezone: string;
  position: Position;
  plan: Plan;
  trialEndsAt: string;
}

const CURRENCY_OPTIONS = CURRENCIES.filter(
  (c, i) => CURRENCIES.findIndex((o) => o.name === c.name) === i,
);
const ownZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;
const zoneOptions = (current: string) => {
  const zones = Intl.supportedValuesOf("timeZone");
  return (zones.includes(current) ? zones : [current, ...zones]).map(
    (value) => ({ value, label: value }),
  );
};
const symbolOf = (code: string) =>
  CURRENCY_OPTIONS.find((c) => c.name === code)?.symbol ?? code;

const initialValues = (
  restaurant: RestaurantRecord | null,
  uiLanguage: string,
): Values => {
  if (!restaurant) {
    const language = PREVIEW_LANGUAGES.some((l) => l.code === uiLanguage)
      ? uiLanguage
      : "en";
    return {
      languages: [language],
      defaultLanguage: language,
      texts: { [language]: { name: "", description: "" } },
      currency: "PLN",
      timezone: ownZone(),
      position: "after",
      plan: "starter",
      trialEndsAt: "",
    };
  }
  const texts: Record<string, Text> = {};
  for (const code of restaurant.supportedLanguages) {
    const found = restaurant.translations.find((x) => x.language === code);
    texts[code] = {
      name: found?.name ?? "",
      description: found?.description ?? "",
    };
  }
  return {
    languages: restaurant.supportedLanguages,
    defaultLanguage: restaurant.supportedLanguages[0] ?? "en",
    texts,
    currency: restaurant.currency,
    timezone: restaurant.timezone,
    position: restaurant.currencySymbolPosition,
    plan: restaurant.plan,
    trialEndsAt: restaurant.trialEndsAt
      ? restaurant.trialEndsAt.slice(0, 10)
      : "",
  };
};

interface BodyProps {
  id: string | undefined;
  restaurant: RestaurantRecord | null;
}

/** The same logo on a light and on a dark surface; `surface` and `inverse` swap with the theme, so the order follows it. */
const LogoPreview = ({ url }: { url: string }) => {
  const { t } = useTranslation("venue");
  const { resolved } = useTheme();
  const tiles = [
    {
      label: t("logo.onLight"),
      tone: resolved === "light" ? "bg-surface" : "bg-inverse",
    },
    {
      label: t("logo.onDark"),
      tone: resolved === "light" ? "bg-inverse" : "bg-surface",
    },
  ];
  return (
    <div
      role="group"
      aria-label={t("logo.preview")}
      className="grid grid-cols-2 gap-3"
    >
      {tiles.map((tile) => (
        <figure
          key={tile.label}
          className={clsx(
            "m-0 flex flex-col items-center gap-2 rounded-card p-3 shadow-hairline",
            tile.tone,
          )}
        >
          <img src={url} alt="" className="size-16 rounded-lg object-cover" />
          <figcaption
            className={clsx(
              "text-center text-caption",
              tile.tone === "bg-inverse" ? "text-on-inverse" : "text-muted",
            )}
          >
            {tile.label}
          </figcaption>
        </figure>
      ))}
    </div>
  );
};

const RestaurantFormBody = ({ id, restaurant }: BodyProps) => {
  const { t, i18n } = useTranslation(["locations", "common"]);
  const { t: tv } = useTranslation("venue");
  const navigate = useNavigate();
  const apiError = useApiError();
  const { hasRole } = useAuth();
  const isAdmin = hasRole(["Admin"]);
  const initial = useRef(initialValues(restaurant, i18n.language)).current;
  const [values, setValues] = useState<Values>(initial);
  const [editLanguage, setEditLanguage] = useState(initial.defaultLanguage);
  const [logo, setLogo] = useState<File | null>(null);
  const [savedLogoUrl, setSavedLogoUrl] = useState(restaurant?.logoUrl ?? null);
  const [logoError, setLogoError] = useState<string>();
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [progress, setProgress] = useState<number>();
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<{
    message: string;
    planLimit: boolean;
  } | null>(null);

  const textOf = (code: string): Text =>
    values.texts[code] ?? { name: "", description: "" };
  const setText = (code: string, patch: Partial<Text>) =>
    setValues((v) => {
      const base: Text = v.texts[code] ?? { name: "", description: "" };
      return { ...v, texts: { ...v.texts, [code]: { ...base, ...patch } } };
    });
  const tooLong = (code: string) =>
    textOf(code).description.trim().length > DESCRIPTION_MAX;
  const incomplete = values.languages.filter(
    (code) => !textOf(code).name.trim() || tooLong(code),
  );
  const currentLanguage = values.languages.includes(editLanguage)
    ? editLanguage
    : values.defaultLanguage;
  const dirty =
    (!id && logo !== null) ||
    JSON.stringify(values) !== JSON.stringify(initial);

  const toggleLanguage = (code: string, on: boolean) =>
    setValues((v) => {
      const languages = on
        ? [...v.languages, code]
        : v.languages.filter((l) => l !== code);
      if (languages.length === 0) return v;
      return {
        ...v,
        languages,
        defaultLanguage: languages.includes(v.defaultLanguage)
          ? v.defaultLanguage
          : languages[0],
      };
    });

  const uploadLogo = async (file: File) => {
    const body = new FormData();
    body.append("file", file);
    setLogo(file);
    setLogoError(undefined);
    setProgress(0);
    try {
      const { data } = await api.post<RestaurantRecord>(
        `/restaurants/${id}/upload-logo`,
        body,
        {
          onUploadProgress: (ev) =>
            setProgress(
              ev.total ? Math.round((ev.loaded / ev.total) * 100) : undefined,
            ),
        },
      );
      setSavedLogoUrl(data.logoUrl);
      notify.success(tv("logo.saved"));
    } catch (err) {
      setLogoError(apiError(err));
    } finally {
      setLogo(null);
      setProgress(undefined);
    }
  };

  const removeLogo = async () => {
    setRemoving(true);
    try {
      await api.delete(`/restaurants/${id}/logo`);
      setSavedLogoUrl(null);
      setConfirmRemove(false);
      notify.success(tv("logo.removed"));
    } catch (err) {
      setConfirmRemove(false);
      setLogoError(apiError(err));
    } finally {
      setRemoving(false);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (incomplete.length > 0) {
      setEditLanguage(incomplete[0]);
      return;
    }
    const languages = [
      values.defaultLanguage,
      ...values.languages.filter((l) => l !== values.defaultLanguage),
    ];
    const main = textOf(values.defaultLanguage);
    const payload = {
      name: main.name.trim(),
      description: main.description.trim(),
      timezone: values.timezone,
      supportedLanguages: languages,
      translations: languages.map((language) => ({
        language,
        name: textOf(language).name.trim(),
        description: textOf(language).description.trim() || null,
      })),
      currency: {
        name: values.currency,
        symbol: symbolOf(values.currency),
        symbolPosition: values.position,
      },
      ...(isAdmin
        ? {
            plan: values.plan,
            trialEndsAt: values.trialEndsAt
              ? new Date(`${values.trialEndsAt}T00:00:00.000Z`).toISOString()
              : null,
          }
        : {}),
    };
    setSaving(true);
    setFailure(null);
    try {
      const { data } = id
        ? await api.put<RestaurantRecord>(`/restaurants/${id}`, payload)
        : await api.post<RestaurantRecord>("/restaurants", payload);
      if (!id && logo) {
        const body = new FormData();
        body.append("file", logo);
        setProgress(0);
        await api.post(`/restaurants/${data.id}/upload-logo`, body, {
          onUploadProgress: (ev) =>
            setProgress(
              ev.total ? Math.round((ev.loaded / ev.total) * 100) : undefined,
            ),
        });
      }
      notify.success(
        t(id ? "restaurants.form.updated" : "restaurants.form.created", {
          name: payload.name,
        }),
      );
      navigate(id ? `/restaurants/${id}` : "/restaurants");
    } catch (err) {
      setFailure({
        message: apiError(err),
        planLimit: asPlanLimit(err) !== null,
      });
    } finally {
      setSaving(false);
      setProgress(undefined);
    }
  };

  const position = values.position;
  const symbol = symbolOf(values.currency);

  return (
    <>
      <form
        onSubmit={submit}
        noValidate
        className="mx-auto flex max-w-3xl flex-col gap-6"
      >
        {failure && (
          <InlineBanner tone="danger">
            <p>{failure.message}</p>
            {failure.planLimit && <p className="mt-1">{t("form.upgrade")}</p>}
          </InlineBanner>
        )}

        <FormSection
          title={t("restaurants.form.brand")}
          description={t("restaurants.form.brandHint")}
        >
          <LanguageTabs
            languages={values.languages}
            value={currentLanguage}
            onChange={setEditLanguage}
            incomplete={submitted ? incomplete : []}
          />
          <Field
            label={t("restaurants.form.name")}
            error={
              submitted && !textOf(currentLanguage).name.trim()
                ? t("form.required")
                : undefined
            }
          >
            {(c) => (
              <Input
                {...c}
                lang={currentLanguage}
                value={textOf(currentLanguage).name}
                onChange={(ev) =>
                  setText(currentLanguage, { name: ev.target.value })
                }
                autoComplete="organization"
              />
            )}
          </Field>
          <Field
            label={t("restaurants.form.description")}
            hint={tv("description.hint")}
            error={
              tooLong(currentLanguage)
                ? tv("description.tooLong", { max: DESCRIPTION_MAX })
                : undefined
            }
            optional
          >
            {(c) => (
              <>
                <Textarea
                  {...c}
                  lang={currentLanguage}
                  rows={3}
                  value={textOf(currentLanguage).description}
                  onChange={(ev) =>
                    setText(currentLanguage, { description: ev.target.value })
                  }
                />
                <p
                  className={clsx(
                    "text-caption tabular-nums",
                    tooLong(currentLanguage) ? "text-danger" : "text-muted",
                  )}
                >
                  {tv("description.counter", {
                    count: textOf(currentLanguage).description.trim().length,
                    max: DESCRIPTION_MAX,
                  })}
                </p>
              </>
            )}
          </Field>
        </FormSection>

        <FormSection
          title={tv("logo.title")}
          description={`${tv("logo.description")} ${tv("logo.hint")}`}
        >
          <FileUpload
            label={tv("logo.title")}
            file={logo}
            currentUrl={id ? savedLogoUrl : undefined}
            canRemove={id !== undefined}
            aspect="1/1"
            progress={progress}
            error={logoError}
            onSelect={id ? uploadLogo : setLogo}
            onRemove={id ? () => setConfirmRemove(true) : () => setLogo(null)}
          />
          {id && savedLogoUrl && logo === null && (
            <LogoPreview url={savedLogoUrl} />
          )}
        </FormSection>

        <FormSection
          title={t("restaurants.form.languages")}
          description={t("restaurants.form.languagesHint")}
        >
          <div
            role="group"
            aria-label={t("restaurants.form.languages")}
            className="grid gap-1 sm:grid-cols-2"
          >
            {PREVIEW_LANGUAGES.map((l) => (
              <Checkbox
                key={l.code}
                checked={values.languages.includes(l.code)}
                onChange={(on) => toggleLanguage(l.code, on)}
                disabled={
                  values.languages.length === 1 &&
                  values.languages.includes(l.code)
                }
                label={<span lang={l.code}>{l.originalLabel}</span>}
              />
            ))}
          </div>
          <Field
            label={t("restaurants.form.defaultLanguage")}
            hint={t("restaurants.form.defaultLanguageHint")}
          >
            {(c) => (
              <Select
                {...c}
                value={values.defaultLanguage}
                onChange={(v) =>
                  setValues((s) => ({ ...s, defaultLanguage: v }))
                }
                options={values.languages.map((code) => ({
                  value: code,
                  label:
                    PREVIEW_LANGUAGES.find((l) => l.code === code)
                      ?.originalLabel ?? code,
                }))}
              />
            )}
          </Field>
        </FormSection>

        <FormSection title={tv("timezone.title")}>
          <Field label={tv("timezone.label")} hint={tv("timezone.hint")}>
            {(c) => (
              <Select
                {...c}
                value={values.timezone}
                onChange={(zone) =>
                  setValues((s) => ({ ...s, timezone: zone }))
                }
                options={zoneOptions(initial.timezone)}
              />
            )}
          </Field>
        </FormSection>

        <FormSection title={t("restaurants.form.currency")}>
          <Field label={t("restaurants.form.currency")}>
            {(c) => (
              <Select
                {...c}
                value={values.currency}
                onChange={(code) =>
                  setValues((s) => ({
                    ...s,
                    currency: code,
                    position:
                      CURRENCY_OPTIONS.find((o) => o.name === code)
                        ?.symbolPosition ?? s.position,
                  }))
                }
                options={CURRENCY_OPTIONS.map((o) => ({
                  value: o.name,
                  label: `${o.name} (${o.symbol})`,
                }))}
              />
            )}
          </Field>
          <div className="flex flex-col gap-1.5">
            <span className="text-small font-medium text-strong">
              {t("restaurants.form.position")}
            </span>
            <Segmented
              aria-label={t("restaurants.form.position")}
              value={position}
              onChange={(p) => setValues((s) => ({ ...s, position: p }))}
              options={[
                { value: "after", label: `12\u00a0${symbol}` },
                { value: "before", label: `${symbol}\u00a012` },
              ]}
            />
          </div>
        </FormSection>

        {isAdmin && (
          <FormSection
            title={t("restaurants.form.plan")}
            description={t("restaurants.form.planHint")}
          >
            <Field label={t("restaurants.form.planLabel")}>
              {(c) => (
                <Select
                  {...c}
                  value={values.plan}
                  onChange={(plan) => setValues((s) => ({ ...s, plan }))}
                  options={PLANS.map((p) => ({
                    value: p,
                    label: t(`common:plans.${p}`),
                  }))}
                />
              )}
            </Field>
            <Field
              label={t("restaurants.form.trialEnds")}
              hint={t("restaurants.form.trialHint")}
              optional
            >
              {(c) => (
                <Input
                  {...c}
                  type="date"
                  value={values.trialEndsAt}
                  onChange={(ev) =>
                    setValues((s) => ({ ...s, trialEndsAt: ev.target.value }))
                  }
                />
              )}
            </Field>
          </FormSection>
        )}

        <FormActions
          onCancel={() => navigate(id ? `/restaurants/${id}` : "/restaurants")}
          dirty={dirty}
          submitting={saving}
          submitLabel={t(id ? "form.save" : "form.create")}
        />
      </form>
      <ConfirmDialog
        open={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        onConfirm={removeLogo}
        busy={removing}
        title={tv("logo.removeTitle")}
        confirmLabel={tv("logo.removeConfirm")}
      >
        {tv("logo.removeBody")}
      </ConfirmDialog>
    </>
  );
};

const RestaurantForm = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(["locations", "common"]);
  const title = t(
    id ? "restaurants.form.editTitle" : "restaurants.form.createTitle",
  );
  usePageTitle(title);
  const { data, state, retry } = useRequest(
    `restaurant-form:${id ?? "new"}`,
    () =>
      id
        ? api.get<RestaurantRecord>(`/restaurants/${id}`).then((r) => r.data)
        : Promise.resolve(null),
  );
  const backTo = id ? `/restaurants/${id}` : "/restaurants";

  return (
    <>
      <PageHeader
        title={title}
        back={{
          to: backTo,
          label: id
            ? t("restaurants.detail.back")
            : t("restaurants.list.title"),
        }}
      />
      {state === "error" && (
        <StateBlock
          variant="error"
          title={t("restaurants.form.loadFailed")}
          action={<Button onClick={retry}>{t("common:ui.retry")}</Button>}
        />
      )}
      {state === "loading" && (
        <SkeletonRegion>
          <Skeleton className="mx-auto h-64 max-w-3xl" />
        </SkeletonRegion>
      )}
      {state === "ready" && (id ? data !== null : true) && (
        <RestaurantFormBody key={id ?? "new"} id={id} restaurant={data} />
      )}
    </>
  );
};

export default RestaurantForm;
